import type { TabScale, TabSong, TabPitchClass } from './types';
const pitches: TabPitchClass[] = [
  'C',
  'C#',
  'D',
  'D#',
  'E',
  'F',
  'F#',
  'G',
  'G#',
  'A',
  'A#',
  'B',
];
/** A suggested practice scale, never a claim about the original recording's key. */
export function suggestTabScale(song: TabSong): TabScale | undefined {
  if (song.scale) return song.scale;
  const histogram = Array(12).fill(0) as number[];
  for (const event of song.events)
    for (const note of event.notes) {
      const pitch =
        (pitches.indexOf(song.tuning.notes[6 - note.string]) + note.fret) % 12;
      histogram[pitch] += event.durationBeats / event.notes.length;
    }
  if (histogram.filter((n) => n > 0).length < 4) return;
  const profiles = [
    {
      name: 'Major (Ionian)' as const,
      weights: [
        6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88,
      ],
    },
    {
      name: 'Minor (Aeolian)' as const,
      weights: [
        6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17,
      ],
    },
  ];
  const mean = histogram.reduce((a, b) => a + b, 0) / 12;
  let best: TabScale | undefined;
  let bestScore = -Infinity;
  for (const profile of profiles)
    for (let root = 0; root < 12; root++) {
      const average = profile.weights.reduce((a, b) => a + b, 0) / 12;
      const norm = Math.sqrt(
        profile.weights.reduce((a, b) => a + (b - average) ** 2, 0)
      );
      const score =
        histogram.reduce(
          (sum, value, pitch) =>
            sum +
            (value - mean) *
              (profile.weights[(pitch - root + 12) % 12] - average),
          0
        ) / norm;
      if (score > bestScore) {
        bestScore = score;
        best = { root: pitches[root], name: profile.name, basis: 'inferred' };
      }
    }
  const norm = Math.sqrt(
    histogram.reduce((sum, value) => sum + (value - mean) ** 2, 0)
  );
  return norm > 0 && bestScore / norm >= 0.5 ? best : undefined;
}
